// src/services/api.ts
import axios from 'axios';
import {
    AuthResponse,
    DashboardData,
    Product,
    Order,
    Wallet,
    Transaction,
    License,
    Customer,
    LeaderboardEntry,
} from '../types';

/** پایهٔ آدرس API — اگر محیطت فرق می‌کنه همین یکی رو تغییر بده */
const API_BASE_URL = 'http://localhost:8000/api/v1/agents';
// const API_BASE_URL = 'https://server.licenseapp.ir/api/v1/agents';

/* ---------------- Axios instance ---------------- */
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: { 'Content-Type': 'application/json' },
});

// Bearer token
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('auth_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// Normalize error messages (FA)
api.interceptors.response.use(
    (res) => res,
    (err) => {
        const msg =
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            'خطای ناشناخته از سرور';
        return Promise.reject(new Error(msg));
    }
);

/* ---------------- Types ---------------- */
export interface AgentProduct {
    id: string;
    title: string;
    description: string;
    price: number;
    price_annual: string | null;
    agent_commission_pct: string; // "30.00"
}

// src/services/api.ts
export const meAPI = {
    getMe: async () => {
        const { data } = await api.get('/me'); // یا /profile یا هر روتی که داری
        return data;
    },

    acceptContract: async () => {
        const { data } = await api.post('/contract/accept'); // روت پیشنهادی
        return data;
    },

    getContract: async () => {
        const { data } = await api.get('/contract'); // متن/HTML قرارداد
        return data;
    },
};

api.interceptors.response.use(
    (res) => res,
    (err) => {
        const status = err?.response?.status;

        if (status === 401 || status === 403 || status === 419) {
            // ✅ به AuthContext خبر بده
            window.dispatchEvent(new Event('app:logout'));

            // ✅ برو لاگین
            if (window.location.pathname !== '/login') {
                window.location.replace('/login');
            }

            return Promise.reject(new Error('نشست شما منقضی شده است. دوباره وارد شوید.'));
        }

        const msg =
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            'خطای ناشناخته از سرور';

        return Promise.reject(new Error(msg));
    }
);

export interface CreateOrderPayload {
    productId: number;
    quantity: number;
}

export interface CreateOrderResponse {
    order: {
        id: string;
        agent_id: string;
        product_id: string;
        quantity: number;
        total_price: string;
        status: string;
        created_at: string;
        updated_at: string;
    };
    invoice: {
        id: number;
        licence_count: string;
        agent_id: number;
        product_id: number;
        price: string;
        status: string;
        created_at: string;
        updated_at: string;
    };
    licenses: Array<{
        id: string;
        agent_id: string;
        invoice_id: number;
        license_key: string;
        issued_at: string;
        expires_at: string;
        created_at: string;
        updated_at: string;
        product_id: number;
        status: string;
        metadata: any;
        plan_id: number | null;
        device_limit: number | null;
        buyer_id: number | null;
    }>;
}

/* ---------------- Auth ---------------- */
export const authAPI = {
    requestOTP: async (phone_number: string): Promise<AuthResponse> => {
        const { data } = await api.post('/auth/login', { phone_number });
        return data;
    },
    verifyOTP: async (phone_number: string, code: string): Promise<AuthResponse> => {
        const { data } = await api.post('/auth/verify', { phone_number, code });
        return data;
    },
    requestAgency: async (formData: FormData) => {
        const { data } = await api.post('/request', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return data;
    },
};

/* ---------------- Dashboard ---------------- */
export const dashboardAPI = {
    getDashboardData: async (period?: 'day' | 'week' | 'month' | 'quarter' | 'year' | 'all'): Promise<DashboardData> => {
        const p = period ? `?period=${period}` : '';
        const { data } = await api.get(`/dashboard${p}`);
        return data;
    },
};

/* ---------------- Products ---------------- */
export const productsAPI = {
    getAgentProducts: async (): Promise<AgentProduct[]> => {
        const { data } = await api.get('/products');
        return data;
    },
    // سازگاری با کد قدیم
    getAllProducts: async (): Promise<Product[]> => {
        const { data } = await api.get('/products');
        return data;
    },
};

/* ---------------- Orders ---------------- */
export const ordersAPI = {
    createOrder: async (payload: CreateOrderPayload): Promise<CreateOrderResponse> => {
        const { data } = await api.post('/orders', payload);
        return data;
    },
    // سازگاری با کد قدیم
    createOrderLegacy: async (productId: number, quantity: number): Promise<CreateOrderResponse> => {
        const { data } = await api.post('/orders', { productId, quantity });
        return data;
    },
    getAllOrders: async (): Promise<Order[]> => {
        const { data } = await api.get('/orders');
        return data;
    },
    // ✅ افزودن متد پرداخت سفارش (که در پشت‌صحنه به پرداخت فاکتور ارجاع می‌دهد)
    payOrder: async (orderId: number): Promise<{ success: boolean; new_balance: number; licenses: any[] }> => {
        const { data } = await api.post(`/orders/${orderId}/pay`);
        return data;
    },
};

/* ---------------- Wallet ---------------- */
export const walletAPI = {
    getWalletInfo: async (): Promise<Wallet> => {
        const { data } = await api.get('/wallet');
        return data;
    },
    getTransactions: async (): Promise<Transaction[]> => {
        const { data } = await api.get('/transactions');
        return data;
    },
    chargeWallet: async (amount: number) => {
        const { data } = await api.post('/wallet/charge', { amount });
        return data;
    },
    verifyCharge: async (authority: string, status: string) => {
        const { data } = await api.get(`/wallet/verify?Authority=${authority}&Status=${status}`);
        return data;
    },
};

/* ---------------- Licenses ---------------- */
export const licensesAPI = {
    getLicenses: async (): Promise<License[] | any> => {
        // بک‌اندت ممکنه {licenses: []} بده یا مستقیم []
        const { data } = await api.get('/licenses');
        return data;
    },

    /** بررسی خریدار بر اساس موبایل (طرح فعلی تو: POST /licenses/check { phone }) */
    checkBuyerByPhone: async (phone: string) => {
        const { data } = await api.post('/licenses/check', { phone });
        return data;
    },

    /** اختصاص با موبایل خریدار (اگر جریان قبلی‌ت این بود) */
    assignLicense: async (payload: {
        licenseId: number;
        firstName?: string;
        lastName?: string;
        phone: string;
        email?: string;
        country?: string;         // اضافه شد
        province?: string | null;
        city?: string | null;
        address?: string | null;
        complexName?: string | null;
        complexNumber?: string | null;
    }) => {
        const { data } = await api.post('/licenses/assign', payload);
        return data;
    },
    /** ✅ اختصاص لایسنس به «شناسهٔ مشتری» (مودال انتخاب مشتری) */
    assignLicenseToCustomer: async (licenseId: number, customerId: number) => {
        // مطابق روتی که گفتی: POST /licenses/assign/:customerId  { licenseId }
        const { data } = await api.post(`/licenses/assign/${customerId}`, { licenseId });
        return data;
    },
};

/* ---------------- Invoices ---------------- */
/**
 * این بخش کامل و هماهنگ با مودال‌ها:
 * - list: برای گرفتن لیست با فیلتر (سمت سرور اگر پشتیبانی کنی)
 * - getCustomers: سازگار با کد قدیمی که خروجی همون /customers رو می‌خواست
 * - checkPhone: قبل از افزودن، کل سیستم رو برای وجود شماره چک می‌کنه
 * - add: افزودن (JSON)
 * - addForm: سازگار با نسخه قدیمی که FormData می‌فرستاد
 */
export type CustomersListParams = {
    q?: string;
    country_id?: string;
    province_id?: string;
    city_id?: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    email?: string;
    page?: number;
    page_size?: number;
};

export const customersAPI = {
    /** لیست مشتری‌ها (با فیلتر اختیاری) */
    list: async (params?: CustomersListParams): Promise<{ customers: Customer[]; total?: number }> => {
        const { data } = await api.get('/customers', { params });
        // اگر بک‌اند فقط آرایه برگردوند:
        if (Array.isArray(data)) return { customers: data };
        return data;
    },

    /** سازگاری با کد قدیمی */
    getCustomers: async (): Promise<Customer[] | any> => {
        const { data } = await api.get('/customers');
        return data;
    },

    /** لیدربورد مشتری/نماینده (اگر مصرف داری) */
    getLeaderboard: async (period?: 'day' | 'week' | 'month' | 'quarter' | 'year' | 'all') => {
        const p = period ? `?period=${period}` : '';
        const { data } = await api.get(`/leaderboard${p}`);
        return data;
    },

    /** ✅ قبل از افزودن، چک وجود شماره در سیستم (پیشنهادی: GET /customers/check?phone=...) */
    checkPhone: async (phone: string): Promise<{ exists: boolean; customer?: any }> => {
        const { data } = await api.get('/customers/check', { params: { phone } });
        return data;
    },

    /** ✅ افزودن مشتری (JSON) — با payloadی که AddCustomerModal می‌فرسته */
    add: async (payload: {
        firstName: string;
        lastName: string;
        phone: string;
        email?: string;
        countryId?: string;
        province?: string;
        city?: string;
        address?: string;
        complexName?: string;
        complexNumber?: string;
    }): Promise<{ success: boolean; customer: Customer }> => {
        const { data } = await api.post('/customers', payload);
        return data;
    },

    /** سازگاری با نسخه قدیمی که FormData می‌فرستاد */
    addCustomer: async (customerData: FormData): Promise<Customer> => {
        const { data } = await api.post('/customers', customerData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return data;
    },
};

// ============================================================
//   NOTIFICATIONS (Agent)
// ============================================================

export const agentNotificationsAPI = {
    /** لیست نوتیفیکیشن‌ها با پارامتر اختیاری */
    async list(params?: { only_unread?: boolean | 0 | 1; page?: number; limit?: number }) {
        const res = await api.get('/notifications', { params });
        return res.data; // { success, items, total, page, limit }
    },

    /** فقط نوتیف‌های خوانده‌نشده */
    async getUnread(params?: { limit?: number }) {
        const res = await api.get('/notifications/unread', { params });
        return res.data; // { success, items }
    },

    /** خلاصه خوانده‌نشده‌ها (برای نوار بالا) */
    async getUnreadSummary() {
        const res = await api.get('/notifications/unread-summary');
        return res.data; // { success, data: { total, orders, invoices, ... } }
    },

    /** مارک کردن گروهی */
    markReadGroup: (group: string) =>
        api.post('/notifications/read-group', { group }).then(r => r.data),

    /** مارک کردن یک یا چند نوتیف */
    markAsRead: (ids: number[]) =>
        api.post('/notifications/read', { ids }).then(r => r.data),
};

// ============================================================
//   INVOICES (Agent) - با پشتیبانی از پرداخت ترکیبی
// ============================================================

export const agentInvoicesAPI = {
    /** لیست فاکتورها */
    list: (params?: { page?: number; limit?: number; status?: string }) =>
        api.get('/invoices', { params }).then(r => r.data),

    /** جزئیات فاکتور */
    getById: (id: number) =>
        api.get(`/invoices/${id}`).then(r => r.data),

    /** ✅ پرداخت کامل فاکتور از کیف پول */
    pay: (invoiceId: number) =>
        api.post(`/invoices/${invoiceId}/pay`).then(r => r.data),

    /** ✅ پرداخت ترکیبی (کیف پول + درگاه) - جدید */
    payCombined: (invoiceId: number) =>
        api.post(`/invoices/${invoiceId}/pay-combined`).then(r => r.data),
};

// ============================================================
//   SUPPORT (تیکت‌ها)
// ============================================================

export type TicketStatus = 'open' | 'pending' | 'answered' | 'closed';
export type TicketPriority = 'low' | 'normal' | 'high' | 'urgent';
export type TicketCategory = 'technical' | 'financial' | 'license' | 'account' | 'other';

export interface TicketMessage {
    id: number;
    body: string;
    sender_type: 'user' | 'admin' | 'system';
    /** برای پیام ادمین همیشه «تیم پشتیبانی» است، نه نام واقعی */
    display_name: string;
    created_at: string;
}

export interface Ticket {
    id: number;
    ticket_no: string;
    subject: string;
    status: TicketStatus;
    status_label: string;
    priority: TicketPriority;
    priority_label: string;
    category: TicketCategory;
    category_label: string;
    message_count: number;
    /** true یعنی توپ در زمین پشتیبانی است */
    awaiting_reply: boolean;
    last_reply_at: string | null;
    created_at: string;
    closed_at: string | null;
    messages?: TicketMessage[];
}

export const supportAPI = {
    /** تیکت‌های خود کاربر */
    list: (params?: { page?: number; pageSize?: number; status?: TicketStatus; search?: string }) =>
        api.get('/support', { params }).then(r => r.data as {
            tickets: Ticket[];
            pagination: { currentPage: number; pageSize: number; total: number; totalPages: number };
        }),

    stats: () =>
        api.get('/support/stats').then(r => r.data.stats as Record<string, number>),

    /** گفتگو — یادداشت‌های داخلی پشتیبانی در این خروجی نیستند */
    getById: (id: number) =>
        api.get(`/support/${id}`).then(r => r.data.ticket as Ticket),

    create: (payload: {
        subject: string;
        body: string;
        category?: TicketCategory;
        priority?: TicketPriority;
    }) => api.post('/support', payload).then(r => r.data.ticket as Ticket),

    /** پاسخ کاربر — تیکت را دوباره به وضعیت «باز» برمی‌گرداند */
    reply: (id: number, body: string) =>
        api.post(`/support/${id}/reply`, { body }).then(r => r.data.ticket as Ticket),

    close: (id: number) =>
        api.post(`/support/${id}/close`).then(r => r.data.ticket as Ticket),
};

export default api;
